import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useBranch } from "@/contexts/BranchContext";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Loader2, Search, ArrowRight, CreditCard, ArrowLeft } from "lucide-react";
import { usePagination } from "@/components/Pagination";
import { toast } from "sonner";

/**
 * Month-end "where does every student stand?" view. One row per student in
 * the selected branch with rolled-up totals plus quick filter chips so the
 * owner can zero in on:
 *   • Students with any outstanding balance
 *   • Students who attended sessions without paying
 *
 * Backed by `GET /api/student-accounts/summary?branch_id=…`.
 */
export default function StudentAccountsSummaryPage() {
  const { selectedBranch } = useBranch();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const currency = selectedBranch?.currency || "₱";
  const fmt = (v) => `${currency}${Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  useEffect(() => {
    if (!selectedBranch?.id) { setLoading(false); return; }
    let alive = true;
    setLoading(true);
    api.get(`/student-accounts/summary?branch_id=${selectedBranch.id}`)
      .then(({ data }) => { if (alive) setItems(data.items || []); })
      .catch((e) => { if (alive) toast.error(formatApiError(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [selectedBranch?.id]);

  const counts = useMemo(() => ({
    all: items.length,
    outstanding: items.filter((s) => s.total_balance > 0).length,
    settled:     items.filter((s) => s.total_balance === 0 && s.record_count > 0).length,
    unpaid_attended: items.filter((s) => s.unpaid_attended_sessions > 0).length,
    no_records:  items.filter((s) => s.record_count === 0).length,
  }), [items]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((s) => {
      if (q && !(s.student_name || "").toLowerCase().includes(q) &&
          !(s.class_name || "").toLowerCase().includes(q)) return false;
      if (filter === "outstanding")     return s.total_balance > 0;
      if (filter === "settled")         return s.total_balance === 0 && s.record_count > 0;
      if (filter === "unpaid_attended") return s.unpaid_attended_sessions > 0;
      if (filter === "no_records")      return s.record_count === 0;
      return true;
    });
  }, [items, search, filter]);

  // Totals row so owners see the "school-wide" outstanding number too.
  const grand = useMemo(() => filtered.reduce((acc, s) => ({
    billed: acc.billed + s.total_billed,
    paid:   acc.paid   + s.total_paid,
    balance:acc.balance+ s.total_balance,
  }), { billed: 0, paid: 0, balance: 0 }), [filtered]);

  const { pageItems, controls } = usePagination(filtered, {
    pageSize: 25, label: "students", resetKey: `${search}|${filter}|${selectedBranch?.id || ""}`,
  });

  const CHIPS = [
    { id: "all",             label: "All",                cls: "border-slate-300 text-slate-700 data-[on=true]:bg-slate-800 data-[on=true]:text-white data-[on=true]:border-slate-800" },
    { id: "outstanding",     label: "Has outstanding",    cls: "border-[#F2B8BA] text-[#C62828] data-[on=true]:bg-[#FDECEA] data-[on=true]:border-[#C62828]" },
    { id: "unpaid_attended", label: "Attended unpaid",    cls: "border-[#D7CDF0] text-[#5E35B1] data-[on=true]:bg-[#EDE7F6] data-[on=true]:border-[#5E35B1]" },
    { id: "settled",         label: "Fully settled",      cls: "border-[#B7E2BB] text-[#2E7D32] data-[on=true]:bg-[#E8F5E9] data-[on=true]:border-[#2E7D32]" },
    { id: "no_records",      label: "No records",         cls: "border-slate-300 text-slate-500 data-[on=true]:bg-slate-200 data-[on=true]:text-slate-800 data-[on=true]:border-slate-400" },
  ];

  return (
    <>
      <div className="space-y-5" data-testid="student-accounts-summary-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <Link to="/students/accounts" className="inline-flex items-center gap-1 text-xs font-heading text-brand-purple hover:underline mb-1">
              <ArrowLeft className="w-3 h-3" /> Student Accounts
            </Link>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold">Account Summary</h1>
            <p className="text-brand-inkMuted text-sm">One row per student — total billed, paid, and outstanding rolled up across all categories.</p>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-brand-inkMuted" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search student or class…"
                className="pl-9 rounded-2xl h-11 w-72"
                data-testid="sa-summary-search"
              />
            </div>
          </div>
        </div>

        {/* Grand-total banner */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-testid="sa-summary-totals">
          <BannerStat tone="blue"  label="Total billed" value={fmt(grand.billed)} />
          <BannerStat tone="green" label="Total collected" value={fmt(grand.paid)} />
          <BannerStat tone={grand.balance > 0 ? "red" : "neutral"} label="Outstanding (filtered)" value={fmt(grand.balance)} />
        </div>

        {/* Filter chips */}
        <div className="flex flex-wrap gap-2" data-testid="sa-summary-chips">
          {CHIPS.map((c) => {
            const on = filter === c.id;
            return (
              <button
                key={c.id}
                type="button"
                data-on={on}
                data-testid={`sa-summary-chip-${c.id}`}
                onClick={() => setFilter(c.id)}
                className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-full text-xs font-heading font-semibold border-2 transition ${c.cls}`}
              >
                {c.label}
                <span className={`inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full text-[10px] font-bold ${on ? "bg-white/25" : "bg-slate-100 text-slate-600"}`}>{counts[c.id] ?? 0}</span>
              </button>
            );
          })}
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-brand-purple/10 shadow-sm overflow-hidden" data-testid="sa-summary-list">
          {loading ? (
            <p className="p-6 text-brand-inkMuted flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</p>
          ) : filtered.length === 0 ? (
            <div className="p-10 text-center">
              <CreditCard className="w-10 h-10 mx-auto text-brand-purple mb-2" />
              <p className="font-heading text-foreground">No students match this view.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F6F8FE] text-brand-inkMuted">
                  <tr>
                    <th className="text-left px-3 py-2.5 font-heading font-semibold">Student</th>
                    <th className="text-left px-3 py-2.5 font-heading font-semibold">Class</th>
                    <th className="text-center px-3 py-2.5 font-heading font-semibold">Records</th>
                    <th className="text-right px-3 py-2.5 font-heading font-semibold">Billed</th>
                    <th className="text-right px-3 py-2.5 font-heading font-semibold">Paid</th>
                    <th className="text-right px-3 py-2.5 font-heading font-semibold">Outstanding</th>
                    <th className="text-center px-3 py-2.5 font-heading font-semibold">Unpaid Sessions</th>
                    <th className="px-3 py-2.5"></th>
                  </tr>
                </thead>
                <tbody>
                  {pageItems.map((s) => (
                    <tr key={s.student_id} className="border-t border-brand-purple/10 hover:bg-[#FAFBFE]" data-testid={`sa-summary-row-${s.student_id}`}>
                      <td className="px-3 py-2.5 font-heading font-semibold">{s.student_name}</td>
                      <td className="px-3 py-2.5 text-brand-inkMuted">{s.class_name || "—"}</td>
                      <td className="px-3 py-2.5 text-center tabular-nums">{s.record_count}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums">{fmt(s.total_billed)}</td>
                      <td className="px-3 py-2.5 text-right tabular-nums text-[#2E7D32]">{fmt(s.total_paid)}</td>
                      <td className={`px-3 py-2.5 text-right tabular-nums font-heading font-bold ${s.total_balance > 0 ? "text-[#C62828]" : "text-foreground"}`}>{fmt(s.total_balance)}</td>
                      <td className="px-3 py-2.5 text-center">
                        {s.unpaid_attended_sessions > 0 ? (
                          <span className="inline-flex items-center justify-center min-w-[1.75rem] h-6 px-2 rounded-full bg-[#FDECEA] text-[#C62828] text-xs font-heading font-bold">
                            {s.unpaid_attended_sessions}
                          </span>
                        ) : (
                          <span className="text-brand-inkMuted">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2.5 text-right">
                        <Link to={`/students?view=${s.student_id}`}
                              className="inline-flex items-center gap-1 text-xs font-heading font-semibold text-brand-purple hover:underline"
                              data-testid={`sa-summary-view-${s.student_id}`}>
                          View student <ArrowRight className="w-3 h-3" />
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-3 pb-2">{controls}</div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function BannerStat({ label, value, tone }) {
  const tones = {
    blue:    "from-[#E3F2FD] to-white text-[#0D47A1]",
    green:   "from-[#E8F5E9] to-white text-[#2E7D32]",
    red:     "from-[#FDECEA] to-white text-[#C62828]",
    neutral: "from-slate-50 to-white text-slate-700",
  };
  return (
    <div className={`rounded-2xl border border-slate-200 bg-gradient-to-br p-4 ${tones[tone] || tones.blue}`}>
      <p className="text-[11px] uppercase tracking-wider font-heading font-semibold opacity-80">{label}</p>
      <p className="mt-1 font-heading font-bold text-2xl tabular-nums">{value}</p>
    </div>
  );
}
